import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { getControl, getGroup } from '@shared/helpers/form.helper';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { getDatiBilancioControl } from '../validators/helpers/dati-bilancio-form.helpers';
import { cloneDeep } from 'lodash-es';
import { debounceTime, merge, startWith } from 'rxjs';
import { Utilities } from '@core/helpers/utilities';
import { isNilOrEmpty, nz } from '@shared/helpers/utils.helper';
import { toNum } from '@shared/helpers/math.helper';

export const datiBilancioSchedaPrestitoListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_DataChiusEser(form, store);
    my_NumSociDataDich(form, store);
    my_CICR(form, store);
};

/** Syncs my_DatiBilancio.my_DataChiusura2 into my_SchedaVerificaPrestito.my_DataChiusEser. */
const my_DataChiusEser = (form: FormGroup, store: Store<Revisione53State>): void => {
    const sourceControl = getControl(form, `${formPaths.datiBilancio}.my_DataChiusura2`);
    const targetControl = getDatiBilancioControl(form, 'my_SchedaVerificaPrestito.my_DataChiusEser');

    sourceControl.valueChanges.pipe(withInitialValueDebounced(sourceControl)).subscribe((value: string) => {
        targetControl.patchValue(value, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaVerificaPrestito.my_DataChiusEser = value;
            return myVerbaleClone;
        });
    });
};

/** Computes my_NumSociDataDich = Nz(SociFisCoopData) + Nz(SociGiuFinData) + Nz(SociGiuCoopData) + Nz(SociSpecData) + Nz(SociFisFinData). */
const my_NumSociDataDich = (form: FormGroup, store: Store<Revisione53State>): void => {
    const effGroup = getGroup(form, formPaths.baseSociale);
    const sociFisCoopControl = getControl(effGroup, 'my_SociFisCoopData');
    const sociGiuFinControl = getControl(effGroup, 'my_SociGiuFinData');
    const sociGiuCoopControl = getControl(effGroup, 'my_SociGiuCoopData');
    const sociSpecControl = getControl(effGroup, 'my_SociSpecData');
    const sociFisFinControl = getControl(effGroup, 'my_SociFisFinData');
    const targetControl = getDatiBilancioControl(form, 'my_SchedaVerificaPrestito.my_NumSociDataDich');

    merge(
        sociFisCoopControl.valueChanges.pipe(startWith(sociFisCoopControl.value)),
        sociGiuFinControl.valueChanges.pipe(startWith(sociGiuFinControl.value)),
        sociGiuCoopControl.valueChanges.pipe(startWith(sociGiuCoopControl.value)),
        sociSpecControl.valueChanges.pipe(startWith(sociSpecControl.value)),
        sociFisFinControl.valueChanges.pipe(startWith(sociFisFinControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const result = (
                nz(sociFisCoopControl.value) +
                nz(sociGiuFinControl.value) +
                nz(sociGiuCoopControl.value) +
                nz(sociSpecControl.value) +
                nz(sociFisFinControl.value)
            ).toString(10);
            targetControl.patchValue(result, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaVerificaPrestito.my_NumSociDataDich = result;
                return myVerbaleClone;
            });
        });
};

const my_CICR = (form: FormGroup, store: Store<Revisione53State>): void => {
    const schedaGroup = getGroup(form, `${formPaths.bilancio}.my_SchedaVerificaPrestito`);
    const patrNettoControl = getControl(schedaGroup, 'my_PatrNettoUltBil');
    const prestitoControl = getControl(schedaGroup, 'my_PrestitoSociale');
    const valoreImmBilControl = getControl(schedaGroup, 'my_ValoreImmBil');
    const valoreImmICIControl = getControl(schedaGroup, 'my_ValoreImmICI');

    merge(
        patrNettoControl.valueChanges.pipe(startWith(patrNettoControl.value)),
        prestitoControl.valueChanges.pipe(startWith(prestitoControl.value)),
        valoreImmBilControl.valueChanges.pipe(startWith(valoreImmBilControl.value)),
        valoreImmICIControl.valueChanges.pipe(startWith(valoreImmICIControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => calcolaCICR(form, store));
};

const calcolaCICR = (form: FormGroup, store: Store<Revisione53State>): void => {
    const schedaGroup = getGroup(form, `${formPaths.bilancio}.my_SchedaVerificaPrestito`);
    const patrNettoRaw: string = getControl(schedaGroup, 'my_PatrNettoUltBil')?.value;
    const prestitoRaw: string = getControl(schedaGroup, 'my_PrestitoSociale')?.value;
    const patrNetto = toNum(patrNettoRaw);
    const prestito = toNum(prestitoRaw);
    const cicrControl = getControl(schedaGroup, 'my_CICR');

    let storeValue = '';
    if (isNilOrEmpty(patrNettoRaw) || isNilOrEmpty(prestitoRaw)) {
        cicrControl?.setValue('', { emitEvent: false });
    } else if (patrNetto === 0) {
        cicrControl?.setValue('', { emitEvent: false });
    } else {
        let cicr = prestito / patrNetto;
        cicr = Math.round(cicr * 100) / 100;
        cicrControl?.setValue(cicr, { emitEvent: false });
        storeValue = cicr.toString(10);
    }

    updateVerbaleOnStore(store, (myVerbale) => {
        const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
        myVerbaleClone.my_SezioneI__Rilevazione.my_Bilancio.my_SchedaVerificaPrestito.my_CICR = storeValue;
        return myVerbaleClone;
    });
};
